import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Card, Table, Tag, Button, Space, Tooltip, Typography, Modal, Form, Input, InputNumber, Switch, Select,
    Popconfirm, Empty, message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import cdnNodeOpsService from '../../services/cdnNodeOpsService';
import DdosTag, { ddosText } from './DdosTag';

const { Text } = Typography;
const HEALTH_COLOR = { up: 'success', down: 'error', unknown: 'default' };

/** IP 子池:目标发布数 N、成员(preferred / weight)、当前发布集合与健康 */
const IpSubPools = ({ onChanged }) => {
    const { t } = useTranslation();
    const [pools, setPools] = useState([]);
    const [defaultTarget, setDefaultTarget] = useState(2);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null); // {} = 新建
    const [adding, setAdding] = useState(null); // pool
    const [candidates, setCandidates] = useState([]);
    const [picked, setPicked] = useState([]);
    const [form] = Form.useForm();

    const healthLabel = useMemo(() => ({ up: t('Up'), down: t('Down'), unknown: t('Unknown') }), [t]);

    const load = useCallback(async () => {
        setLoading(true);
        const r = await cdnNodeOpsService.listIpPools();
        setLoading(false);
        if (r.success) {
            setPools(r.data?.pools || []);
            setDefaultTarget(r.data?.default_target || 2);
        } else message.error(r.error || t('Failed to load'));
    }, [t]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, [load]);

    const done = (r, ok) => {
        if (r.success) { load(); onChanged && onChanged(); if (ok) ok(); } else message.error(r.error || t('Save failed'));
    };

    const openEdit = (p) => {
        setEditing(p || {});
        form.setFieldsValue({
            name: p?.name || '', description: p?.description || '',
            publish_count_v4: p?.publish_count_v4 ?? null, publish_count_v6: p?.publish_count_v6 ?? 0,
            active: (p?.status || 'active') === 'active',
        });
    };

    const savePool = async () => {
        const v = await form.validateFields();
        const r = await cdnNodeOpsService.saveIpPool({
            id: editing?.id, name: v.name, description: v.description,
            publish_count_v4: v.publish_count_v4 ?? '', publish_count_v6: v.publish_count_v6 ?? 0,
            status: v.active ? 'active' : 'inactive',
        });
        done(r, () => setEditing(null));
    };

    const openAdd = async (p) => {
        setAdding(p);
        setPicked([]);
        // 一个 IP 可在多个子池:候选 = 不在本子池的全部 IP
        const r = await cdnNodeOpsService.getIpPool({});
        if (r.success) setCandidates((r.data?.items || []).filter((c) => !(c.pools || []).some((x) => x.id === p.id)));
    };

    const candidateOptions = useMemo(() => {
        const byNode = {};
        candidates.forEach((c) => {
            (byNode[c.node_name] = byNode[c.node_name] || []).push({
                value: c.id,
                label: `${c.ip}${c.ddos_gbps ? ` · ${ddosText(t, c.ddos_gbps)}` : ''}${c.health === 'down' ? ` (${healthLabel.down})` : ''}${c.pools?.length ? ` · ${c.pools.map((x) => x.name).join(', ')}` : ''}`,
            });
        });
        return Object.entries(byNode).map(([node, options]) => ({ label: node, options }));
    }, [candidates, healthLabel, t]);

    const addMembers = async () => {
        if (!picked.length) return;
        done(await cdnNodeOpsService.addPoolMembers(adding.id, picked), () => setAdding(null));
    };

    const memberColumns = [
        { title: t('Node'), dataIndex: 'node_name', render: (n, m) => <Space size={4}>{n}{m.node_status !== 'active' && <Tag>{m.node_status}</Tag>}</Space> },
        {
            title: 'IP', dataIndex: 'ip',
            render: (ip, m) => <Space size={4}><Text code>{ip}</Text><DdosTag gbps={m.ddos_gbps} />{m.family === 'ipv6' && <Tag>IPv6</Tag>}{m.anycast && <Tag color="purple">Anycast</Tag>}{m.published && <Tag color="blue">DNS</Tag>}</Space>,
        },
        {
            title: t('Health'), dataIndex: 'health',
            render: (h, m) => (
                <Tooltip title={m.results?.length ? m.results.map((x) => <div key={x.v}>{x.ok ? '✓' : '✗'} {x.v} · {x.ok ? `${x.ms}ms` : (x.err || x.code)}</div>) : t('No probe results yet')}>
                    <Space size={4}>
                        <Tag color={HEALTH_COLOR[h]}>{healthLabel[h] || h}</Tag>
                        {m.votes > 0 && <Text type="secondary" style={{ fontSize: 12 }}>{m.votes_ok}/{m.votes}</Text>}
                    </Space>
                </Tooltip>
            ),
        },
        {
            title: t('Preferred'), dataIndex: 'preferred', width: 90,
            render: (v, m) => (
                <Button type="link" size="small" icon={v ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                    onClick={async () => done(await cdnNodeOpsService.updatePoolMember({ id: m.id, preferred: !v }))} />
            ),
        },
        {
            title: t('Weight'), dataIndex: 'weight', width: 100,
            render: (w, m) => (
                <InputNumber size="small" min={0} max={1000} defaultValue={w} style={{ width: 80 }}
                    onBlur={async (e) => { const v = Number(e.target.value); if (v !== w && !Number.isNaN(v)) done(await cdnNodeOpsService.updatePoolMember({ id: m.id, weight: v })); }} />
            ),
        },
        {
            title: '', key: 'op', width: 40,
            render: (_, m) => (
                <Popconfirm title={t('Remove from pool?')} onConfirm={async () => done(await cdnNodeOpsService.removePoolMember(m.id))}>
                    <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            ),
        },
    ];

    return (
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)}>{t('New pool')}</Button>
            {!loading && pools.length === 0 && <Empty />}
            {pools.map((p) => (
                <Card
                    key={p.id}
                    size="small"
                    loading={loading && !pools.length}
                    title={
                        <Space wrap size={8}>
                            <strong>{p.name}</strong>
                            {p.status !== 'active' && <Tag>{t('Disabled')}</Tag>}
                            {[['IPv4', p.v4], ['IPv6', p.v6]].filter(([, f]) => f && (f.target > 0 || f.members > 0)).map(([label, f]) => (
                                <Tooltip key={label} title={t('Published / target · healthy')}>
                                    <Tag color={f.target === 0 ? 'default' : (f.published < f.target ? 'error' : 'success')}>
                                        {label} {f.published} / {f.target} · {f.healthy}
                                    </Tag>
                                </Tooltip>
                            ))}
                            {p.lines.map((l) => <Tag key={l.id} color="cyan">{l.label}</Tag>)}
                        </Space>
                    }
                    extra={
                        <Space size={0}>
                            <Button type="link" size="small" icon={<PlusOutlined />} onClick={() => openAdd(p)}>{t('Add IP')}</Button>
                            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(p)} />
                            <Popconfirm title={t('Delete this pool?')} onConfirm={async () => done(await cdnNodeOpsService.deleteIpPool(p.id))}>
                                <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                        </Space>
                    }
                >
                    {p.description && <Text type="secondary">{p.description}</Text>}
                    <Table size="small" rowKey="id" columns={memberColumns} dataSource={p.members} pagination={false} />
                </Card>
            ))}

            <Modal title={editing?.id ? editing.name : t('New pool')} open={!!editing} onCancel={() => setEditing(null)} onOk={savePool} destroyOnClose>
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label={t('Name')} rules={[{ required: true }]}><Input maxLength={64} /></Form.Item>
                    <Form.Item name="description" label={t('Description')}><Input maxLength={255} /></Form.Item>
                    <Space size={24}>
                        <Form.Item name="publish_count_v4" label={`IPv4 · ${t('IPs to publish')}`} tooltip={t('Empty = default ({{n}})', { n: defaultTarget })}>
                            <InputNumber min={0} max={100} placeholder={String(defaultTarget)} />
                        </Form.Item>
                        <Form.Item name="publish_count_v6" label={`IPv6 · ${t('IPs to publish')}`} tooltip={t('0 = do not publish IPv6')}>
                            <InputNumber min={0} max={100} />
                        </Form.Item>
                    </Space>
                    <Form.Item name="active" label={t('Enabled')} valuePropName="checked"><Switch /></Form.Item>
                </Form>
            </Modal>

            <Modal title={`${adding?.name || ''} · ${t('Add IP')}`} open={!!adding} onCancel={() => setAdding(null)} onOk={addMembers} okButtonProps={{ disabled: !picked.length }} destroyOnClose width={560}>
                <Select mode="multiple" style={{ width: '100%' }} value={picked} onChange={setPicked} options={candidateOptions}
                    optionFilterProp="label" placeholder="IP" maxTagCount="responsive" />
            </Modal>
        </Space>
    );
};

export default IpSubPools;
